import {
	type User,
	type UserAssignSelectUserFragment,
	type UserAssignSelectAvailabilityQuery,
	UserType,
} from '@/generated/graphql';
import { UserAssignSelectSize } from './constants';
import {
	UserAssignGroups,
	type PinnedUserPositions,
	type UserAssignGroup,
	type UserAssignSelectRole,
	type RoleSlotItem,
	RoleSlotType,
} from './types';
import type { ValueOf } from '@/types';

export const getInitials = (name: string): string => {
	const normalized = name.trim().replace(/\s+/g, ' ');

	if (!normalized) {
		return '';
	}

	const words = normalized.split(' ');

	if (words.length > 1) {
		return words.map((word) => word.charAt(0).toUpperCase()).join('');
	}

	const singleWord = words[0];
	return singleWord.substring(0, 3).toUpperCase();
};

const makeSortString = (user: UserAssignSelectUserFragment): string => {
	const isCupo = user.type === UserType.Cupo ? 'B' : 'A';
	const formattedName = (user.name || user.lastName || '')
		.trim()
		.toUpperCase();

	return isCupo + formattedName;
};

/**
 * Sorts users alphabetically with CUPO users appearing after regular users.
 *
 * @param users - Array of users to sort
 * @returns Sorted array of users (does not mutate original array)
 */
export const sortUsers = (
	users: UserAssignSelectUserFragment[]
): UserAssignSelectUserFragment[] => {
	return [...users].sort((a, b) => {
		const sortA = makeSortString(a);
		const sortB = makeSortString(b);

		return sortA.localeCompare(sortB);
	});
};

export const getUserTooltip = (
	user: UserAssignSelectUserFragment | undefined | null
): string => {
	return `${user?.name} ${user?.role?.name ? `(${user.role.name})` : ''}`;
};

export const getUserProfileImage = (
	user: UserAssignSelectUserFragment | undefined | null,
	size: ValueOf<typeof UserAssignSelectSize>
): string => {
	if (!user) {
		return '';
	}

	return size === UserAssignSelectSize.LARGE
		? (user.profilePicture?.medium ?? '')
		: (user.profilePicture?.mini ?? '');
};

export const getUserAvailability = (
	userId: User['id'],
	taskUsersAvailability: UserAssignSelectAvailabilityQuery['TaskUsersAvailability']
):
	| UserAssignSelectAvailabilityQuery['TaskUsersAvailability'][number]
	| undefined => {
	return taskUsersAvailability.find(
		(availability) => availability.user.id === userId
	);
};

export const getUserRoleTooltip = (
	groupId: UserAssignGroup['id'],
	user: UserAssignSelectUserFragment
): string | null => {
	if (
		groupId === UserAssignGroups.ProjectMembers ||
		groupId === UserAssignGroups.Other
	) {
		return user.role?.name ?? null;
	}

	return null;
};

/**
 * Sorts selected roles and pinned users into a combined list for header display.
 * Roles and pinned users are sorted alphabetically by role name.
 */
export const sortSelectedRoles = ({
	users,
	selectedRoles,
	pinnedUserPositions,
}: {
	users: UserAssignSelectUserFragment[];
	selectedRoles: UserAssignSelectRole[];
	pinnedUserPositions: PinnedUserPositions;
}): RoleSlotItem[] => {
	const items: RoleSlotItem[] = [];

	for (const role of selectedRoles) {
		items.push({ type: RoleSlotType.Role, id: `role-${role.id}`, role });
	}

	for (const [userId, replacedRole] of pinnedUserPositions.entries()) {
		const user = users.find((u) => u.id === userId);
		if (user) {
			items.push({
				type: RoleSlotType.PinnedUser,
				id: `pinned-${user.id}`,
				user,
				replacedRole,
			});
		}
	}

	return items.sort((a, b) => {
		const nameA =
			a.type === RoleSlotType.Role ? a.role.name : a.replacedRole.name;
		const nameB =
			b.type === RoleSlotType.Role ? b.role.name : b.replacedRole.name;
		return nameA.localeCompare(nameB);
	});
};

/**
 * Sorts selected users for header display.
 * Current user appears first, remaining users maintain their original order.
 * Pinned users are excluded from this list.
 */
export const sortSelectedUsers = ({
	users,
	pinnedUserPositions,
	currentUserId,
}: {
	users: UserAssignSelectUserFragment[];
	pinnedUserPositions: PinnedUserPositions;
	currentUserId?: User['id'];
}): UserAssignSelectUserFragment[] => {
	const pinnedUserIds = new Set(pinnedUserPositions.keys());

	return [...users]
		.filter((user) => !pinnedUserIds.has(user.id))
		.sort((a, b) => {
			if (a.id === currentUserId) {
				return -1;
			}

			if (b.id === currentUserId) {
				return 1;
			}

			return 0;
		});
};
